import { useState, useEffect, useRef, useMemo } from "react";
import classnames from "classnames";
import { useTable, useSortBy, SortingRule, Column } from "react-table";
import { Friendly } from "../../common/Friendly";
import { ChampionLink } from "../../common/ChampionLink";
import { TableScrollHandler, useLazyLoadTableRows, TIER_LIST_PAGE_SIZE } from "../../common/TableScrollHandler";

import { useResponsive } from "@outplayed/responsive";
import { getRiotAssetsContext } from "@outplayed/riot-assets";
import { TooltipContainer } from "@outplayed/tooltips";
import { numberWithCommas } from "@ugg/shared/utils/math";
import { ArenaTierListChampionInfo } from "@ugg/shared/api/data-parser/tier-lists/arena-tier-list";
import { getTierColor, getLuTierColor, getLuTier } from "@ugg/shared/utils/tier-helpers";
import { round } from "@ugg/shared/utils/math";
import { ArenaModeType } from "@ugg/shared/utils/queue-type-helpers";

interface ArenaTierListTableProps {
  data: ArenaTierListChampionInfo[];
  arenaModeType: ArenaModeType;
}

export function ArenaTierListTable(props: ArenaTierListTableProps) {
  const { getChampionName } = getRiotAssetsContext();
  const prevSort = useRef<SortingRule<ArenaTierListChampionInfo>[] | null>(null);
  const [pageSize, setPageSize] = useState(TIER_LIST_PAGE_SIZE);
  const { currentBreakpoint } = useResponsive();

  const { data, arenaModeType } = props;

  const tableData = useMemo(() => {
    return data || [];
  }, [data]);

  const isMobile = ["MOBILE_SMALL", "MOBILE_MEDIUM", "MOBILE_LARGE"].includes(currentBreakpoint);
  const isMobileSmall = ["MOBILE_SMALL", "MOBILE_MEDIUM"].includes(currentBreakpoint);

  const hiddenColumns = [
    isMobile && "best_with",
    isMobileSmall && "pick_rate",
    isMobileSmall && "avg_placement",
    isMobileSmall && "top1_rate",
  ]
    .filter((col) => !!col)
    .map((col) => String(col));

  const columns = useMemo<Column<ArenaTierListChampionInfo>[]>(
    () => [
      {
        id: "rank",
        accessor: "champion_id",
        Header: "Rank",
        responsiveClassName: "xxxs:w-[40px] xs:w-[60px] xxxs:min-w-[40px] xs:min-w-[40px] xxxs:max-w-[40px] xs:max-w-[60px]",
        headerClassName: "rank xxxs:max-xs:invisible",
        className: "rank",
        Cell: ({ index }) => <span>{index + 1}</span>,
        sortable: false,
      },
      {
        Header: "Champion",
        accessor: "champion_id",
        responsiveClassName: `
          xxxs:min-w-[40px] xs:min-w-[175px] xs:max-w-[200px]
          max-[800px]:!min-w-[70px] max-[800px]:!w-[70px] 
          max-[800px]:!flex-[70] max-[800px]:!justify-center
        `,
        headerClassName: "champion xxxs:max-xs:invisible",
        className: "champion xs:!pl-[12px] xs:!pr-[12px]",
        sortInverted: true,
        sortType: (a, b) => {
          const champNameA = getChampionName(a.values.champion_id);
          const champNameB = getChampionName(b.values.champion_id);
          return champNameB.localeCompare(champNameA);
        },
        Cell: ({ value }) => (
          <ChampionLink
            className="max-[800px]:[&_.champion-name]:hidden max-[800px]:!pl-0"
            championId={Number(value)}
            dim={isMobileSmall ? 24 : 30}
          />
        ),
      },
      {
        Header: (props) => (
          <TooltipContainer
            tooltipInfo={
              <span>
                The U.GG tier list is derived from standard deviations on
                <br />
                champion winrates, pick rates and ban rates to identify the
                <br />
                strongest champions in the current meta.
              </span>
            }
          >
            <div>Tier</div>
          </TooltipContainer>
        ),
        id: "tier",
        accessor: "tier",
        responsiveClassName: "xxxs:min-w-[40px] xs:min-w-[50px] xxxs:max-w-[70px] xs:max-w-[90px]",
        headerClassName: "tier",
        className: "tier",
        sortDescFirst: true,
        sortType: (a, b) => {
          const stdevsA = a.values.tier.stdevs;
          const stdevsB = b.values.tier.stdevs;
          const pickA = a.values.tier.pick_rate;
          const pickB = b.values.tier.pick_rate;
          const pickrate = 0;

          if (stdevsA > stdevsB) {
            if (pickA > pickrate && pickB > pickrate) return 1;
            else if (pickA > pickrate && pickB <= pickrate) return 1;
            else if (pickA <= pickrate && pickB > pickrate) return -1;
            else if (pickA <= pickrate && pickB <= pickrate) {
              if (stdevsA > stdevsB) return 1;
              else if (stdevsA < stdevsB) return -1;
            }
          } else if (stdevsA < stdevsB) {
            if (pickA > pickrate && pickB > pickrate) return -1;
            else if (pickA > pickrate && pickB <= pickrate) return 1;
            else if (pickA <= pickrate && pickB > pickrate) return -1;
            else if (pickA <= pickrate && pickB <= pickrate) {
              if (stdevsA > stdevsB) return 1;
              else if (stdevsA < stdevsB) return -1;
            }
          }
          return 0;
        },
        Cell: ({ value }) => (
          <span className={getLuTierColor(Number(value.stdevs.toFixed(2)))}>
            <b>{getLuTier(Number(value.stdevs.toFixed(2)))}</b>
          </span>
        ),
      },
      {
        Header: "Avg. Place",
        accessor: "avg_placement",
        responsiveClassName: "xxxs:min-w-[70px] xxxxs:max-w-[90px]",
        headerClassName: "avg-placement",
        className: "avg-placement",
        sortDescFirst: true,
        sortType: (a, b) => {
          return a.values.avg_placement - b.values.avg_placement;
        },
        Cell: ({ value }) => <span>{round(value, 2).toFixed(2)}</span>,
      },
      {
        Header: "1st Rate",
        accessor: "top1_rate",
        headerClassName: "first_rate",
        responsiveClassName: "xxxs:min-w-[80px] xxxxs:max-w-[90px]",
        className: "first_rate",
        sortDescFirst: true,
        sortType: (a, b) => {
          return a.values.top1_rate - b.values.top1_rate;
        },
        Cell: ({ value }) => <span>{value.toFixed(1) + "%"}</span>,
      },
      {
        Header: arenaModeType === 16 ? "Top 4 Rate" : "Top 2 Rate",
        accessor: "top2_rate",
        headerClassName: "top2_rate",
        responsiveClassName: "xxxs:min-w-[80px] xxxxs:max-w-[90px]",
        className: "top2_rate",
        sortDescFirst: true,
        sortType: (a, b) => {
          return a.values.top2_rate - b.values.top2_rate;
        },
        Cell: ({ value }) => (
          <span className={getTierColor(round(value, 1))}>
            <b>{round(value, 1).toFixed(1) + "%"}</b>
          </span>
        ),
      },
      {
        Header: "Pick Rate",
        accessor: "pick_rate",
        headerClassName: "pickrate",
        responsiveClassName: "xxxs:min-w-[80px] xxxxs:max-w-[90px]",
        className: "pickrate",
        sortDescFirst: true,
        sortType: (a, b) => {
          return a.values.pick_rate - b.values.pick_rate;
        },
        Cell: ({ value }) => <span>{value.toFixed(1) + "%"}</span>,
      },
      {
        Header: "Good With",
        accessor: "best_with",
        headerClassName: "good-with",
        responsiveClassName: `xxxs:min-w-[160px] xxxs:max-w-[180px]`,
        className: "good-with",
        disableSortBy: true,
        Cell: ({ value }) => <Friendly dim={24} data={value.good_with} championId={Number(value.champion_id)} showCount={5} />,
      },
      {
        Header: "Matches",
        accessor: "matches",
        headerClassName: "matches",
        responsiveClassName: "xxxs:min-w-[80px] xxxxs:max-w-[90px]",
        className: "matches",
        sortDescFirst: true,
        Cell: ({ value }) => <span>{numberWithCommas(value)}</span>,
      },
    ],
    [currentBreakpoint, arenaModeType],
  );

  const columnStyle = () => ({
    flex: `1 1 100%`,
  });

  const { state, getTableProps, getTableBodyProps, headerGroups, rows, prepareRow, setHiddenColumns } = useTable(
    {
      columns,
      data: tableData,
      initialState: {
        sortBy: prevSort.current || [{ id: "top2_rate", desc: true }],
        hiddenColumns,
      },
      disableSortRemove: true,
    },
    useSortBy,
  );

  useEffect(() => {
    setHiddenColumns(hiddenColumns);
  }, [JSON.stringify(hiddenColumns)]);

  useEffect(() => {
    setPageSize(TIER_LIST_PAGE_SIZE);
  }, [tableData]);

  const tableRef = useRef<HTMLDivElement>(null);
  useLazyLoadTableRows({
    tableData,
    tableRef,
    pageSize,
    setPageSize,
  });

  useEffect(() => {
    if (prevSort.current) {
      if (prevSort.current[0].id !== state.sortBy[0].id || prevSort.current[0].desc !== state.sortBy[0].desc) {
        prevSort.current = state.sortBy;
        setPageSize(TIER_LIST_PAGE_SIZE);
      }
    } else {
      prevSort.current = state.sortBy;
    }
  }, [state.sortBy]);

  return (
    <div className="tier-list-container">
      <div
        ref={tableRef}
        {...getTableProps({
          className: "content-section ReactTable ugg-table-2 tier-list",
        })}
      >
        <div className={`rt-thead -header`}>
          {headerGroups.map((headerGroup, index) => (
            <div {...headerGroup.getHeaderGroupProps({ className: "rt-tr" })}>
              {headerGroup.headers.map((column, index) => {
                const thClassNames = classnames([
                  "rt-th",
                  column.headerClassName,
                  column.responsiveClassName,
                  column.isSorted && !column.isSortedDesc ? "-sort-asc" : "",
                  column.isSorted && column.isSortedDesc ? "-sort-desc" : "",
                ]);

                return (
                  <div
                    className={thClassNames}
                    {...column.getHeaderProps(
                      column.getSortByToggleProps({
                        style: columnStyle(),
                      }),
                    )}
                  >
                    {column.render("Header")}
                  </div>
                );
              })}
            </div>
          ))}
        </div>
        <div {...getTableBodyProps({ className: "rt-tbody" })}>
          {rows.map((row, i) => {
            if (i >= pageSize) {
              return null;
            }
            prepareRow(row);
            return (
              <div key={i} className="rt-tr-group">
                <div {...row.getRowProps({ className: "rt-tr" })}>
                  {row.cells.map((cell, index) => {
                    const tdClassNames = classnames("rt-td", cell.column.responsiveClassName, cell.column.className, {
                      sorted: cell.column.isSorted,
                      "is-in-odd-row": i % 2 === 0,
                    });

                    return (
                      <div
                        {...cell.getCellProps({
                          className: tdClassNames,
                          style: columnStyle(),
                        })}
                      >
                        {cell.render("Cell", { index: i })}
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      </div>
      <TableScrollHandler />
    </div>
  );
}
